import type { FormEvent } from "react";
import { Alerta, Badge, Button, DataList, Desplegable, MontoField, Row, Stack, TextField, type ColumnaDataList } from "@/components";
import type { GastosACubrir, ItemACubrir, VistaPreviaLlamado } from "@/lib/api";
import { textoPropuesta, type ErroresLlamado, type FormLlamado } from "@/lib/aportes";
import { montoAInput } from "@/lib/gastos";
import { formatFecha, formatMoneda } from "@/lib/format";
import { formatParticipacion } from "@/lib/participacion";

type LineaPrevia = VistaPreviaLlamado["lineas"][number];

/** Propuesta del monto desde los gastos a pagar (sólo en el alta). */
export interface PropuestaMonto {
  hasta: string;
  cambiarHasta: (hasta: string) => void;
  datos: GastosACubrir | null;
  error: string | null;
  usar: (() => void) | null; // null: no hay nada que proponer
}

interface Props {
  form: FormLlamado;
  cambiar: <K extends keyof FormLlamado>(campo: K, valor: FormLlamado[K]) => void;
  errores: ErroresLlamado;
  vistaPrevia: VistaPreviaLlamado | null;
  errorVistaPrevia: string | null;
  onSubmit: (e: FormEvent) => void;
  onCancelar: () => void;
  guardando: boolean;
  error: string | null;
  textoGuardar: string;
  propuesta?: PropuestaMonto;
  /** Emitido desde Pedido de capital: el monto (y lo de cada uno) no se edita. */
  montosAMano?: boolean;
}

const COLUMNAS_A_CUBRIR: ColumnaDataList<ItemACubrir>[] = [
  { clave: "fecha", titulo: "Fecha", render: (i) => (i.fecha ? formatFecha(i.fecha) : "Sin fecha de escritura") },
  { clave: "concepto", titulo: "Concepto", render: (i) => i.concepto },
  { clave: "proveedor", titulo: "Proveedor", render: (i) => i.proveedor ?? "—" },
  { clave: "saldo", titulo: "Falta pagar", render: (i) => formatMoneda(i.saldo_usd, "USD") },
];

function BloquePropuesta({ propuesta: p }: { propuesta: PropuestaMonto }) {
  return (
    <Stack espacio={2}>
      <TextField
        etiqueta="Gastos a pagar hasta"
        type="date"
        value={p.hasta}
        onChange={(e) => p.cambiarHasta(e.target.value)}
        ayuda="El monto se propone con lo que falta pagar de los gastos hasta esa fecha (y de la compra del terreno sin confirmar). Podés corregirlo."
      />
      {p.datos && (
        <Row espacio={2} envolver>
          <p className="text-chico">{textoPropuesta(p.datos)}</p>
          {p.usar && (
            <Button variante="secundario" tamanio="sm" onClick={p.usar}>
              Usar {formatMoneda(p.datos.total_usd, "USD")}
            </Button>
          )}
        </Row>
      )}
      {p.datos && p.datos.items.length > 0 && (
        <Desplegable titulo="Ver los gastos">
          <DataList etiqueta="Gastos a pagar" columnas={COLUMNAS_A_CUBRIR} filas={p.datos.items.map((item, n) => ({ ...item, n }))} claveFila={(i) => i.n} />
        </Desplegable>
      )}
      <Alerta texto={p.error} />
    </Stack>
  );
}

/** Alta y edición de un llamado, con la vista previa del reparto que devuelve el backend. */
export function FormularioLlamado({ form, cambiar, errores, vistaPrevia, errorVistaPrevia, onSubmit, onCancelar, guardando, error, textoGuardar, propuesta, montosAMano = false }: Props) {
  const columnas: ColumnaDataList<LineaPrevia>[] = [
    { clave: "nombre", titulo: "Fiduciante", render: (l) => l.nombre },
    { clave: "clase", titulo: "Clase", render: (l) => <Badge tono={l.clase === "B" ? "alerta" : "neutro"}>{l.clase}</Badge> },
    { clave: "porcentaje", titulo: "Participación", render: (l) => formatParticipacion(l.porcentaje) },
    { clave: "pedido", titulo: "Le corresponde", render: (l) => formatMoneda(l.pedido_usd, "USD") },
  ];
  // Sin vista previa (al editar sin tocar monto ni fecha) igual se puede cambiar lo cubierto que ya tenía.
  const mostrarCubierto = !montosAMano && (vistaPrevia ? vistaPrevia.parte_a_usd !== "0.00" : form.cubiertoElegido);
  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack espacio={4}>
        <Row espacio={3} envolver>
          <TextField etiqueta="Fecha" type="date" value={form.fecha} onChange={(e) => cambiar("fecha", e.target.value)} error={errores.fecha} />
          <TextField etiqueta="Vencimiento" type="date" value={form.vencimiento} onChange={(e) => cambiar("vencimiento", e.target.value)} error={errores.vencimiento} />
        </Row>
        {propuesta && <BloquePropuesta propuesta={propuesta} />}
        <MontoField
          etiqueta="Monto"
          valor={form.monto_usd}
          onCambio={(v) => cambiar("monto_usd", v)}
          moneda="USD"
          error={errores.monto_usd}
          deshabilitado={montosAMano}
          ayuda={montosAMano ? "Se armó con montos redondos desde Armar previsión: para cambiarlos, borralo y armalo de nuevo." : "Las previsiones se piden en USD"}
        />
        <TextField etiqueta="Descripción" ayuda="Opcional, ej.: hormigón de la losa 3" value={form.descripcion} onChange={(e) => cambiar("descripcion", e.target.value)} error={errores.descripcion} />
        {vistaPrevia && (
          <Stack espacio={2}>
            <p className="text-chico text-texto-suave">Reparto según la distribución vigente desde {formatFecha(vistaPrevia.vigente_desde)}</p>
            {vistaPrevia.parte_b_usd !== "0.00" && (
              <p className="text-chico">
                Parte clase B (siempre): <span className="tabular-nums">{formatMoneda(vistaPrevia.parte_b_usd, "USD")}</span> · Parte clase A:{" "}
                <span className="tabular-nums">{formatMoneda(vistaPrevia.parte_a_usd, "USD")}</span>
              </p>
            )}
            <DataList etiqueta="Reparto previsto" columnas={columnas} filas={vistaPrevia.lineas} claveFila={(l) => `${l.fiduciante_id}-${l.clase}`} />
          </Stack>
        )}
        {mostrarCubierto && (
          <MontoField
            etiqueta="Cubrir con ventas"
            valor={form.cubiertoElegido ? form.cubierto : montoAInput(vistaPrevia?.cubierto_con_ventas_usd ?? "0")}
            onCambio={(v) => {
              cambiar("cubiertoElegido", true);
              cambiar("cubierto", v);
            }}
            moneda="USD"
            error={errores.cubierto}
            ayuda={
              vistaPrevia
                ? `Plata de ventas disponible: ${formatMoneda(vistaPrevia.disponible_ventas_usd, "USD")}. Sugerido: ${formatMoneda(vistaPrevia.sugerido_usd, "USD")}; podés bajarlo para dejar un colchón. Lo cubierto no se les pide a los clase A.`
                : "Parte clase A que paga la caja con plata de las ventas"
            }
          />
        )}
        <Alerta texto={errorVistaPrevia} />
        <Alerta texto={error} />
        <Row justificar="fin" espacio={2}>
          <Button variante="secundario" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" cargando={guardando}>
            {textoGuardar}
          </Button>
        </Row>
      </Stack>
    </form>
  );
}
